import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
// React 19 native metadata
import { ArrowRight, ChevronRight } from 'lucide-react';
import { useBreadcrumbSchema } from '../common/useBreadcrumbSchema';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComparisonIndex() {
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);

  // SEO: BreadcrumbList JSON-LD for hub indexing
  useBreadcrumbSchema('compare-index', useMemo(() => [
    { name: 'Home', path: '/' },
    { name: 'Comparisons', path: '/compare' },
  ], []));

  useEffect(() => {
    fetch(`${API_URL}/api/comparisons`)
      .then(r => r.json())
      .then(d => setComparisons(d.comparisons || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "iSupplyAI Comparisons — AI Marketing Tool Comparisons",
    "description": "Compare iSupplyAI's multi-agent AI strategy platform with leading marketing tools. Side-by-side feature comparisons with honest analysis.",
    "url": "https://isupplyai.com/compare",
    "publisher": { "@type": "Organization", "name": "iSupplyAI" },
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="comparison-index">
      <>
        <title>iSupplyAI vs Competitors — Comparisons 2026 | iSupplyAI</title>
        <meta name="description" content="Compare iSupplyAI with Jasper, Copy.ai, HubSpot AI, MarketMuse, Surfer SEO, and more. Honest side-by-side comparisons of AI marketing tools." />
        <link rel="canonical" href="https://isupplyai.com/compare" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="iSupplyAI vs Competitors — AI Marketing Tool Comparisons" />
        <meta property="og:description" content="Compare iSupplyAI with Jasper, Copy.ai, HubSpot AI, MarketMuse, Surfer SEO, and more. Honest side-by-side comparisons." />
        <meta property="og:url" content="https://isupplyai.com/compare" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content="iSupplyAI vs Competitors — AI Marketing Tool Comparisons" />
        <meta name="twitter:description" content="Compare iSupplyAI with Jasper, Copy.ai, HubSpot AI, MarketMuse, Surfer SEO, and more." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </>

      {/* Breadcrumbs */}
      <div className="max-w-5xl mx-auto px-4 pt-8">
        <nav className="flex items-center gap-2 text-sm text-gray-500" data-testid="comparison-index-breadcrumbs">
          <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-300">Comparisons</span>
        </nav>
      </div>

      {/* Header */}
      <header className="max-w-5xl mx-auto px-4 pt-8 pb-12">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="comparison-index-title">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">iSupplyAI</span> vs Competitors — AI Marketing Tool Comparisons
        </h1>
        <p className="text-gray-400 text-lg max-w-2xl" data-testid="comparison-index-subtitle">
          Honest, detailed comparisons of iSupplyAI's multi-agent AI strategy platform against leading marketing tools. We show strengths on both sides.
        </p>
      </header>

      {/* Comparison Grid */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="comparison-grid">
            {comparisons.map(c => (
              <Link key={c.slug} to={`/compare/${c.slug}`} className="group p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-purple-500/30 transition-all hover:bg-white/[0.05]" data-testid={`comparison-card-${c.slug}`}>
                <div className="text-xs text-purple-400 font-medium mb-2">{c.category}</div>
                <h2 className="text-lg font-bold text-white group-hover:text-purple-300 transition-colors mb-2">
                  iSupplyAI vs {c.competitor_name}
                </h2>
                <p className="text-sm text-gray-400 leading-relaxed mb-4 line-clamp-2">{c.meta_description}</p>
                <span className="inline-flex items-center gap-1 text-sm text-purple-400 group-hover:text-purple-300 transition-colors">
                  View comparison <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
